import moment from 'moment-timezone';
import _ from 'lodash';
import i18n from '~/utils/i18n';
import Debug from '~/utils/debug';
import { FILTER_TYPES, FLOWS, STATUSES } from '~/utils/constants';
import { availableFilters as genericFilters, visibleFields } from './generic.js';

export const endpoint = 'creatives/search';
const debug = Debug('Creative'); // eslint-disable-line

export const allFields = {
  id: {
    label: 'id',
    sortable: true,
    translateColumn: () => i18n.t('creative_field.title.id'),
  },

  client_language: {
    sortable: false,
    formatter: (value, key, data) => {
      const val = _.get(data, 'campaign.customer.language', null);
      return i18n.t(`${'terms.language.name'}.${val}`);
    },
    translateColumn: () => i18n.t('campaign_field.title.client_language'),
  },
  campaign: {
    label: 'campaign',
    sortable: true,
    formatter: (value) => _.get(value, 'name', null),
    translateColumn: () => i18n.t('creative_field.title.campaign-name'),
  },
  campaign_status: {
    label: 'campaign',
    sortable: false,
    formatter: (value, key, data) => {
      let res = i18n.t('fn.complex.status', { code: `${data.campaign.baseStatus.code}` });
      if (data.campaign.baseStatus.code === STATUSES.ONL && _.get(data.campaign, 'issues', []).filter(i => !i.finished_at).length) {
        res = i18n.t('fn.complex.status', { code: `${data.campaign.baseStatus.code}-issues` });
      }
      return res;
    },
    translateColumn: () => i18n.t('creative_field.title.campaign-status'),
  },
  number_of_calls: {
    label: 'number_of_calls',
    sortable: false,
    formatter: (value, key, data) => {
      const campaign = _.get(data, 'campaign', null);
      if (campaign && campaign.start_date && campaign.confirmed_at && _.get(campaign, 'baseStatus.flow_type', null) !== FLOWS.ASAP) {
        const diff = moment(campaign.start_date).diff(moment(campaign.confirmed_at), 'days');
        return diff > 40 ? 2 : 1;
      }
      return '';
    },
    translateColumn: () => i18n.t('creative_field.title.number_of_calls'),
  },
  second_call_date: {
    label: 'second_call_date',
    sortable: false,
    formatter: (value, key, data) => {
      const campaign = _.get(data, 'campaign', null);
      if (campaign && campaign.start_date && campaign.confirmed_at && _.get(campaign, 'baseStatus.flow_type', null) !== FLOWS.ASAP) {
        return i18n.t('fn.date', { timestamp: moment(campaign.start_date).subtract(23, 'days').toISOString() });
      }
      return '';
    },
    translateColumn: () => i18n.t('creative_field.title.second_call_date'),
  },
  onboarded: {
    label: 'onboarded',
    sortable: false,
    formatter: (value, key, data) => {
      const onboarded = _.get(data, 'details', []).filter(arr => arr.name === 'onboarded')[0];
      if (onboarded) {
        return parseInt(_.get(onboarded, 'value', 0), 10) ? 'Y' : 'N';
      }
      return '';
    },
    translateColumn: () => i18n.t('creative_field.title.onboarded'),
  },
  remote_id: {
    label: 'remote_id',
    sortable: false,
    newKey: true,
    newKeyFrom: 'campaign.customer.remote_id',
    translateColumn: () => i18n.t('creative_field.title.remote-id'),
  },
  customer_name: {
    label: 'company',
    newKey: true,
    newKeyFrom: 'campaign.customer.name',
    sortable: false,
    formatter: (value) => value,
    translateColumn: () => i18n.t('campaign_field.title.customer-name'),
  },
  code: {
    label: 'code',
    sortable: false,
    newKey: true,
    newKeyFrom: 'campaign.code',
    translateColumn: () => i18n.t('creative_field.title.campaign-code'),
  },
  status: {
    label: 'status',
    sortable: true,
    translateColumn: () => i18n.t('creative_field.title.status'),
    translateCell: item => i18n.t('fn.complex.creative-flow-statuses-title', { slug: item }),
  },
  goal_type: {
    label: 'goal_type',
    newKey: true,
    newKeyFrom: 'campaign.campaign_rule.goal_type',
    translateColumn: () => i18n.t('creative_field.title.goal_type'),
    translateCell: item => i18n.t('fn.complex.goal_type', { goal_type: item }),
  },
  created_at: {
    label: 'created-at',
    sortable: true,
    translateColumn: () => i18n.t('creative_field.title.created-at'),
    translateCell: (data) => {
      if (data !== null && moment(data).isValid()) {
        return i18n.t('fn.date', { timestamp: data });
      }
      return null;
    },
  },
  due_date: {
    sortable: false,
    newKey: true,
    newKeyFrom: null,
    formatter: (creative) => {
      const curStatus = _.get(creative, `statuses.${creative.status}`, null);
      if (curStatus && curStatus.duration > 0 && curStatus.finished_at === null
        && curStatus.started_at !== null && moment(curStatus.started_at).isValid()) {
        return i18n.t('fn.date', { timestamp: moment(curStatus.started_at).add(curStatus.duration, 'day').toISOString() });
      }
      return '';
    },
    translateColumn: () => i18n.t('creative_field.title.due-date'),
  },
  status_started: {
    sortable: false,
    newKey: true,
    newKeyFrom: null,
    formatter: (creative) => {
      const curStatus = _.get(creative, `statuses.${creative.status}`, null);
      if (curStatus && curStatus.finished_at === null
        && curStatus.started_at !== null && moment(curStatus.started_at).isValid()) {
        return i18n.t('fn.date', { timestamp: moment(curStatus.started_at).toISOString() });
      }
      return '';
    },
    translateColumn: () => i18n.t('creative_field.title.status-started'),
  },
  days_in_current_status: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.days-in-current-status'),
  },
  working_days_in_current_status: {
    sortable: false,
    translateColumn: () => i18n.t('campaign_field.title.working-days-in-current-status'),
  },
  type: {
    label: 'type',
    sortable: true,
    translateColumn: () => i18n.t('creative_field.title.type'),
    translateCell: item => i18n.t('fn.complex.format', { format: item }),
  },
  operator: {
    label: 'operator',
    sortable: false,
    formatter: (value) => _.get(value, 'username', null),
    translateColumn: () => i18n.t('creative_field.title.operator-username'),
  },
  number_of_reworks: {
    sortable: false,
    translateColumn: () => i18n.t('creative_field.title.number_of_reworks'),
  },
  active: {
    label: 'operator',
    sortable: false,
    formatter: value => i18n.t(`pos.search-table.${value ? 'yes' : 'no'}`),
    translateColumn: () => i18n.t('creative_field.title.active'),
  },
  reject_reason_categories: {
    sortable: false,
    formatter: value => (value ? value.join(', ') : ''),
    translateColumn: () => i18n.t('campaign_field.title.reject-reasons'),
  },
};

export function availableFields(disabledFields) {
  return visibleFields(disabledFields, allFields);
}

/*
Customer SAP ID (customer remote id)
Customer Name
Campaign ID (label change: “Code”) (campaign code)
Creative Status
Campaign Type (label change: “Goal Type”)
Received Date (created_at)
Start Date of Current Creative Status
Days in Current Creative Status
*/
export const enabledFields = [
  'remote_id',
  'customer_name',
  'code',
  'status',
  'goal_type',
  'created_at',
  'status_started',
  'days_in_current_status',
  'working_days_in_current_status',
];

export const enabledFieldsSM = [
  'created_at',
  'campaign',
  'status',
  'goal_type',
];

export const availableFilters = [
  {
    slug: 'campaignName',
    type: FILTER_TYPES.TEXT_INPUT,
    default: null,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('campaign_field.title.name'),
  },
  {
    slug: 'statuses',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/creative-flows',
    formatGetData: flows => {
      const statuses = [];
      const existingSlugs = [];
      flows.forEach(flow => {
        flow.statuses.forEach(s => {
          if (existingSlugs.indexOf(s) < 0) {
            existingSlugs.push(s);
            statuses.push({
              name: i18n.t('fn.complex.creative-flow-statuses-title', { slug: s }),
              slug: s,
            });
          }
        });
      });
      return statuses.length > 0 ? statuses : [];
    },
    toObject: (value, data) => data.filter(c => value.indexOf(c.slug) >= 0),
    toFilter: value => value.map(c => c.slug),
    translateItemLabel: item => i18n.t('fn.complex.creative-flow-statuses-title', { slug: item.slug }),
    translateFilter: () => i18n.t('campaign_field.title.status'),
    options: {
      label: 'name',
      trackBy: 'slug',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    slug: 'type',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'creatives/formats',
    formatGetData: result => result.map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateFilter: () => i18n.t('creative_field.title.type'),
    translateItemLabel: item => i18n.t('fn.complex.format', { format: item.name }),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    slug: 'clientLanguage',
    name: 'client_language',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'config/public',
    formatGetData: result => result.interface_languages.map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateFilter: () => i18n.t('campaign_field.title.client_language'),
    translateItemLabel: (item) => i18n.t(`${'terms.language.name'}.${item.name}`),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    slug: 'customer',
    type: FILTER_TYPES.TEXT_INPUT,
    default: null,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('campaign_field.title.customer'),
  },
  {
    slug: 'numberOfReworks',
    type: FILTER_TYPES.TEXT_INPUT,
    default: null,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('creative_field.title.number_of_reworks'),
  },
  {
    slug: 'daysInCurrentStatus',
    type: FILTER_TYPES.TEXT_INPUT,
    default: null,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('campaign_field.title.days-in-current-status'),
  },
  {
    slug: 'rejectReasons',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'creatives/reject-reasons',
    formatGetData: (result) => {
      const r = result.map(c => ({
        name: c.key,
        value: c.filter,
      }));
      const returnResult = r;
      returnResult.unshift({
        name: i18n.t('pos.search-table.reject-reason.any'),
        value: '*',
      });
      return returnResult;
    },
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateFilter: () => i18n.t('campaign_field.title.reject-reasons'),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'onlyForActiveCampaigns',
    slug: 'onlyForActiveCampaigns',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: null,
    filterData: [
      { name: 'no', value: 'no' },
    ],
    toObject: (value, data) => data.find(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.value,
    translateFilter: () => i18n.t('pos.search-table.filter-creatives-only-for-active-campaigns'),
    translateItemLabel: item => i18n.t(`pos.search-table.${item.name}`),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: false,
    },
  },
  ...genericFilters,
];



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/search-models/creative.js